"use client";

import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { motion } from "framer-motion";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import PageHero from "@/components/sections/PageHero";
import { HiUser } from "react-icons/hi";
import { BsArrowsFullscreen } from "react-icons/bs";
import { IoBed, IoTime, IoSparkles } from "react-icons/io5";
import { getRoomsData } from "@/data/rooms";
import { useLanguage, useTranslations } from "@/lib/i18n";
import { COLORS } from "@/lib/design-tokens";

export default function RoomsClient() {
  const { language } = useLanguage();
  const t = useTranslations();
  const roomsData = getRoomsData(language);

  const handleBooking = (roomName: string) => {
    const message =
      language === "id"
        ? `Halo Admin KinKen Kost Elite, saya tertarik dengan tipe ${roomName}. Boleh info ketersediaan untuk Harian/Bulanan?`
        : `Hello KinKen Kost Elite Admin, I'm interested in the ${roomName} type. May I have availability info for Daily/Monthly?`;
    window.open(
      `https://wa.me/6281916401702?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  const pageContent =
    language === "id"
      ? {
          title: "Koleksi Kamar Premium",
          subtitle:
            "Temukan privasi absolut dengan sentuhan kemewahan Bali. Setiap kamar di KinKen dirancang seperti apartemen pribadi mini dengan layanan concierge 24/7.",
          superText: "The Collection",
          type: "Tipe",
          size: "Luas",
          capacity: "Kapasitas",
          bedType: "Tipe Bed",
          premiumAmenities: "Fasilitas Premium",
          checkAvailability: "Cek Ketersediaan",
          shortStay: "Short Stay",
          bestValue: "Best Value",
        }
      : {
          title: "Premium Room Collection",
          subtitle:
            "Discover absolute privacy with Balinese luxury touch. Every room at KinKen is designed like a mini private apartment with 24/7 concierge service.",
          superText: "The Collection",
          type: "Type",
          size: "Size",
          capacity: "Capacity",
          bedType: "Bed Type",
          premiumAmenities: "Premium Amenities",
          checkAvailability: "Check Availability",
          shortStay: "Short Stay",
          bestValue: "Best Value",
        };

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        title={pageContent.title}
        subtitle={pageContent.subtitle}
        image="/images/rooms/room-hero.webp"
        superText={pageContent.superText}
      />

      <section className="py-20 md:py-32">
        <div className="container mx-auto px-6 md:px-12 space-y-24 md:space-y-32">
          {roomsData.map((room, index) => {
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={room.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className={`flex flex-col md:flex-row gap-10 md:gap-20 items-center ${
                  !isEven ? "md:flex-row-reverse" : ""
                }`}
              >
                {/* IMAGE COLUMN */}
                <div className="w-full md:w-1/2 relative h-[500px] md:h-[600px] group overflow-hidden rounded-xl shadow-2xl">
                  <Image
                    src={room.images.main}
                    alt={room.name}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-105"
                  />

                  {/* PRICE CARD */}
                  <div className="absolute bottom-6 left-6 right-6 md:right-auto md:min-w-[340px] z-20">
                    <div className="rounded-xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.2)]">
                      {/* DAILY */}
                      <div className="bg-white/95 backdrop-blur-sm p-5 border-b border-gray-100 flex justify-between items-center">
                        <div>
                          <div className="flex items-center gap-1.5 text-gray-400 mb-1">
                            <IoTime size={12} />
                            <span className="text-[10px] font-bold uppercase tracking-widest">
                              {pageContent.shortStay}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-sm font-serif text-gray-500 font-medium">
                              IDR
                            </span>
                            <span className="text-2xl font-serif text-brand-charcoal font-bold tracking-tight">
                              {room.prices.daily.value}
                            </span>
                          </div>
                        </div>
                        <span className="text-[10px] text-gray-400 font-medium bg-gray-50 px-2 py-1 rounded-full border border-gray-100">
                          / {room.prices.daily.unit}
                        </span>
                      </div>

                      {/* MONTHLY */}
                      <div className="bg-brand-charcoal p-5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-brand-gold rounded-full blur-[40px] opacity-20 pointer-events-none"></div>

                        <div className="relative z-10 flex justify-between items-end">
                          <div>
                            <div className="flex items-center gap-1.5 text-brand-gold mb-1">
                              <IoSparkles size={12} />
                              <span className="text-[10px] font-bold uppercase tracking-widest">
                                {pageContent.bestValue}
                              </span>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-sm font-serif text-white/60 font-medium">
                                IDR
                              </span>
                              <span className="text-3xl font-serif text-white font-bold tracking-tight">
                                {room.prices.monthly.value}
                              </span>
                            </div>
                          </div>
                          <span className="text-[10px] text-white/50 font-medium mb-1.5">
                            / {room.prices.monthly.unit}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* INFO COLUMN */}
                <div className="w-full md:w-1/2 space-y-8">
                  <div className="space-y-3">
                    <span className="text-brand-gold text-xs font-bold tracking-[0.2em] uppercase">
                      {pageContent.type} 0{room.id}
                    </span>
                    <h2 className="font-serif text-3xl md:text-5xl text-brand-charcoal leading-tight">
                      {room.name}
                    </h2>
                  </div>

                  <p className="text-gray-500 font-light text-lg leading-relaxed">
                    {room.description.long}
                  </p>

                  {/* SPECS */}
                  <div className="grid grid-cols-2 gap-y-6 gap-x-4 py-8 border-t border-b border-gray-100">
                    <div className="flex items-center gap-4 group">
                      <div className="p-3 bg-gray-50 rounded-full text-brand-gold group-hover:bg-brand-gold group-hover:text-white transition-colors">
                        <BsArrowsFullscreen size={20} />
                      </div>
                      <div>
                        <span className="block text-[10px] text-gray-400 uppercase tracking-wider">
                          {pageContent.size}
                        </span>
                        <span className="text-sm font-medium text-brand-charcoal">
                          {room.specs.size}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 group">
                      <div className="p-3 bg-gray-50 rounded-full text-brand-gold group-hover:bg-brand-gold group-hover:text-white transition-colors">
                        <HiUser size={20} />
                      </div>
                      <div>
                        <span className="block text-[10px] text-gray-400 uppercase tracking-wider">
                          {pageContent.capacity}
                        </span>
                        <span className="text-sm font-medium text-brand-charcoal">
                          {room.specs.capacity}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 group col-span-2">
                      <div className="p-3 bg-gray-50 rounded-full text-brand-gold group-hover:bg-brand-gold group-hover:text-white transition-colors">
                        <IoBed size={20} />
                      </div>
                      <div>
                        <span className="block text-[10px] text-gray-400 uppercase tracking-wider">
                          {pageContent.bedType}
                        </span>
                        <span className="text-sm font-medium text-brand-charcoal">
                          {room.specs.bed}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Amenities List */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-xs uppercase tracking-widest text-brand-charcoal">
                      {pageContent.premiumAmenities}
                    </h4>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {room.features.amenities.map((item, idx) => (
                        <li
                          key={idx}
                          className="flex items-center gap-3 text-sm text-gray-500 font-light"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-gold" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-4 flex gap-4">
                    <Button
                      onClick={() => handleBooking(room.name)}
                      className="w-full md:w-auto rounded-none bg-brand-charcoal text-white hover:bg-brand-gold px-10 py-7 uppercase text-xs font-bold tracking-[0.2em] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                    >
                      {pageContent.checkAvailability}
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <Footer />
    </main>
  );
}
